import * as m from '@staiter/i18n';
import {
  type ReconciliationPreviewLine,
  StockReconciliationDraftInput,
  type StockReconciliationKind,
} from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Decimal } from 'decimal.js-light';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Plus,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { EntityRefCell, PageHeader, Table } from '~/components/data';
import { ItemPicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, DateInput, Input, Select, WizardStepper } from '~/components/primitives';
import { applyValidationErrors } from '~/lib/api/apply-validation-errors';
import { fetchItem, fetchWarehouses } from '~/lib/api/inventory';
import { fetchStockBalance } from '~/lib/api/stock';
import {
  SR_VARIANCE_REASONS,
  createStockReconciliationDraft,
  previewStockReconciliation,
} from '~/lib/api/stock-reconciliation';
import { formatDate, formatDecimal, formatMoney, formatUom } from '~/lib/format';
import { formatSignedQty, parseQty, qtyDelta } from '~/lib/format/qty-delta';
import { useZodForm } from '~/lib/forms/useZodForm';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import registryStyles from './_auth.stock-reconciliation.module.css';
import styles from './_auth.stock-reconciliation.new.module.css';

/**
 * Наибольшее отклонение по строкам — считается ЗДЕСЬ, а не приходит с сервера.
 *
 * Прежняя схема ждала поле `max_single_variance`, которого ручка не отдаёт
 * (одно из восьми расхождений, вскрытых гейтом C). Значение выводится из тех
 * же строк, что уже лежат в таблице ниже — без второго источника правды.
 */
function maxVarianceOf(lines: ReadonlyArray<{ variance_qty: string }>): string {
  /*
   * Раунд 11 гейта C — самое крупное отклонение возвращается СО ЗНАКОМ.
   *
   * Модуль брался раньше не для сравнения, а для вывода: сводка показывала
   * «20» там, где в таблице стояло «−20». Знак здесь — не оформление, а
   * содержание: недостача и излишек — разные события с разными последствиями.
   * Модуль остаётся в СРАВНЕНИИ — самое крупное по величине, но выводится как есть.
   */
  let extreme = 0;
  for (const l of lines) {
    const v = Number(l.variance_qty);
    if (Number.isFinite(v) && Math.abs(v) > Math.abs(extreme)) extreme = v;
  }
  return String(extreme);
}

export const Route = createFileRoute('/_auth/stock-reconciliation/new')({
  staticData: { minMainWidth: 960 },
  component: StockReconciliationNew,
});

function varianceOptions() {
  return [
    { value: '', label: '—' },
    ...SR_VARIANCE_REASONS.map((reason) => ({
      value: reason,
      label: m.route_sr_new_variance_label({ reason }),
    })),
  ];
}

function severityLabel(severity: ReconciliationPreviewLine['severity']): string {
  return m.route_sr_severity_label({ severity });
}

const LineSchema = z.object({
  item_id: z.string().min(1, m.route_sr_new_err_item_required()),
  batch_no: z.string().default(''),
  expected_qty: z.string().default('0'),
  physical_qty: z.string().default('0'),
  variance_reason: z.string().default(''),
  /** Единица — справочная, из карточки позиции; в тело запроса не идёт. */
  uom: z.string().default(''),
  /**
   * F53 (WHS-F35): контракт сервера требует ставку оценки строки и партию id, а не
   * номер. Обе приходят из строки остатка вместе с ожидаемым количеством.
   */
  batch_id: z.string().default(''),
  valuation_rate: z.string().default('0'),
  valuation_currency: z.string().default(''),
});

const FormSchema = z.object({
  kind: z.enum(['standard', 'opening_stock']),
  period: z.string().default(''),
  warehouse_id: z.string().min(1, m.route_sr_new_err_warehouse_required()),
  lines: z.array(LineSchema).min(1),
  notes: z.string().default(''),
});
type FormValues = z.infer<typeof FormSchema>;

function emptyLine(): z.infer<typeof LineSchema> {
  return {
    item_id: '',
    batch_no: '',
    expected_qty: '0',
    physical_qty: '0',
    variance_reason: '',
    uom: '',
    batch_id: '',
    valuation_rate: '0',
    valuation_currency: '',
  };
}

type Step = 1 | 2 | 3 | 4;

function StockReconciliationNew(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);

  // Wizard-level state (step, draftId) — остаётся useState.
  const [step, setStep] = useState<Step>(1);
  const [draftId, setDraftId] = useState<string | null>(null);
  /** Гейт F59 (E2-UX-1): номер созданного черновика — называется и ведёт в карточку. */
  const [draftNo, setDraftNo] = useState<string | null>(null);

  const form = useZodForm(FormSchema, {
    kind: 'standard',
    period: '',
    warehouse_id: '',
    lines: [emptyLine()],
    notes: '',
  });
  const { control, register, watch, getValues, setValue, trigger, formState } = form;
  const { errors } = formState;

  const linesArr = useFieldArray({ control, name: 'lines' });

  const openingAlreadyExists = !!user?.tenant_config?.onboarding_date;

  const warehousesQuery = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => fetchWarehouses(),
  });

  const warehouseOptions = useMemo(
    () => [
      { value: '', label: m.route_sr_new_wh_placeholder() },
      ...(warehousesQuery.data?.items ?? []).map((w) => ({
        value: w.id,
        label: `${w.code} — ${w.name}`,
      })),
    ],
    [warehousesQuery.data],
  );

  const warehouseId = watch('warehouse_id');
  const kind = watch('kind');
  const lines = watch('lines');

  const loadExpectedQty = async (idx: number, itemId: string): Promise<void> => {
    if (!itemId || !warehouseId) return;
    try {
      const res = await fetchStockBalance({ item: itemId, warehouse: warehouseId });
      const row = (res.items ?? [])[0];
      if (row) {
        setValue(`lines.${idx}.expected_qty`, row.qty);
        setValue(`lines.${idx}.uom`, row.uom || 'pcs');
        if (row.batch_no) setValue(`lines.${idx}.batch_no`, row.batch_no);
        setValue(`lines.${idx}.batch_id`, row.batch_id ?? '');
        setValue(`lines.${idx}.valuation_rate`, row.valuation_rate ?? '0');
        setValue(`lines.${idx}.valuation_currency`, row.currency ?? '');
      } else {
        setValue(`lines.${idx}.expected_qty`, '0');
        // Гейт F59 (E-UX-1): позиции без остатка единица берётся из карточки,
        // а не остаётся правдоподобным «шт».
        const item = await fetchItem(itemId);
        setValue(`lines.${idx}.uom`, item.stock_uom ?? '');
      }
    } catch {
      /* silent */
    }
  };

  /*
   * F37 (WHS-F37): отклонение — в Decimal. Во float «320 − 322.7894736842» давало
   * «−2.7894736842000043»: хвост арифметики, а не количество.
   */
  const computeVariance = (line: z.infer<typeof LineSchema>): Decimal =>
    qtyDelta(line.physical_qty, line.expected_qty);

  const previewQuery = useQuery({
    queryKey: ['stock-reconciliation', draftId, 'preview'],
    queryFn: () => previewStockReconciliation(draftId ?? ''),
    enabled: !!draftId,
  });

  const createMut = useMutation({
    /*
     * F53 (WHS-F35): тело — по контракту `StockReconciliationDraftInput`. Мастер слал
     * легаси-форму (`kind`, `lines[].physical_qty`, без ставки), сервер отвечал 422 —
     * черновик не сохранялся вовсе. Проверка — той же схемой, что у сервера.
     */
    mutationFn: async (data: FormValues) => {
      // Валюта ставки — из строки остатка. Позиция без остатка идёт со ставкой 0, и
      // валюта её ничего не меняет: берём валюту соседней строки, иначе KZT.
      const entityCurrency =
        data.lines.find((l) => l.valuation_currency)?.valuation_currency || 'KZT';
      const body = StockReconciliationDraftInput.parse({
        reconciliation_type: data.kind === 'opening_stock' ? 'opening_stock' : 'physical_count',
        posting_datetime: new Date().toISOString(),
        warehouse_id: data.warehouse_id,
        ...(data.notes.trim() ? { remarks: data.notes.trim() } : {}),
        items: data.lines.map((l, i) => ({
          position: i,
          item_id: l.item_id,
          warehouse_id: data.warehouse_id,
          ...(l.batch_id ? { batch_id: l.batch_id } : {}),
          expected_qty: l.expected_qty || '0',
          counted_qty: l.physical_qty || '0',
          variance_reason: l.variance_reason || null,
          valuation_rate: {
            amount: l.valuation_rate || '0',
            currency: l.valuation_currency || entityCurrency,
          },
        })),
      });
      const res = await createStockReconciliationDraft(body);
      const h = res.header as { id: string; voucherNo?: string; voucher_no?: string };
      return { id: h.id, voucherNo: h.voucherNo ?? h.voucher_no ?? null };
    },
    onSuccess: (doc) => {
      toast.push({
        variant: 'success',
        title: m.route_sr_new_toast_draft_created(),
        description: m.route_sr_new_toast_draft_created_desc(),
      });
      void qc.invalidateQueries({ queryKey: ['stock-reconciliation', 'docs'] });
      setDraftId(doc.id);
      setDraftNo(doc.voucherNo);
    },
    onError: (err: Error) => {
      if (applyValidationErrors(form, err)) return;
      toast.push({
        variant: 'error',
        title: m.route_sr_new_toast_create_failed(),
        description: err.message,
      });
    },
  });

  const missingReasonsAfterSave = (previewQuery.data?.lines ?? []).filter(
    (l) => l.reason_required,
  ).length;

  const canGoNextStep1 = (): boolean => {
    if (!warehouseId.trim()) return false;
    if (kind === 'opening_stock' && openingAlreadyExists) return false;
    return true;
  };

  const goNext = async (): Promise<void> => {
    if (step === 1) {
      const ok = await trigger(['warehouse_id', 'kind']);
      if (ok && canGoNextStep1()) setStep(2);
      return;
    }
    if (step === 2) {
      const ok = await trigger('lines');
      if (ok) setStep(3);
      return;
    }
    if (step === 3) {
      createMut.mutate(getValues());
      setStep(4);
      return;
    }
  };

  const goBack = (): void => {
    if (step > 1) setStep((step - 1) as Step);
  };

  const totalVarianceDec = lines.reduce((sum, l) => sum.plus(computeVariance(l)), new Decimal(0));
  const totalVariance = totalVarianceDec.toNumber();
  const varianceCounts = lines.reduce(
    (acc, l) => {
      const v = computeVariance(l);
      const base = parseQty(l.expected_qty).abs();
      if (v.isZero()) acc.match += 1;
      else if (v.abs().lt((base.isZero() ? new Decimal(1) : base).times('0.02'))) acc.warn += 1;
      else acc.danger += 1;
      return acc;
    },
    { match: 0, warn: 0, danger: 0 },
  );

  return (
    <div className={styles.container}>
      <PageHeader
        title={
          kind === 'opening_stock'
            ? m.route_sr_new_title_opening()
            : m.route_sr_new_title_standard()
        }
        description={m.route_sr_new_description()}
      />

      <WizardStepper
        ariaLabel={m.route_sr_new_stepper_aria()}
        current={step}
        steps={[
          { n: 1, label: m.route_sr_new_step_label({ step: 'params' }) },
          { n: 2, label: m.route_sr_new_step_label({ step: 'lines' }) },
          { n: 3, label: m.route_sr_new_step_label({ step: 'preview' }) },
          { n: 4, label: m.route_sr_new_step_label({ step: 'done' }) },
        ]}
      />

      {step === 1 && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>{m.route_sr_new_params_heading()}</h2>
          {kind === 'opening_stock' && !openingAlreadyExists && (
            <div className={styles.onboardingBanner} role="status">
              <strong>{m.route_sr_new_onboarding_title()}</strong>
              {m.route_sr_new_onboarding_body()}
            </div>
          )}
          <div className={shared.formGrid3}>
            <Controller
              control={control}
              name="kind"
              render={({ field, fieldState }) => (
                <Select
                  label={m.route_sr_new_field_kind()}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value as StockReconciliationKind)}
                  options={[
                    { value: 'standard', label: m.route_sr_new_kind_standard() },
                    {
                      value: 'opening_stock',
                      label: m.route_sr_new_kind_opening_stock(),
                      disabled: openingAlreadyExists,
                    },
                  ]}
                  error={fieldState.error?.message}
                  hint={openingAlreadyExists ? m.route_sr_new_opening_already_set() : undefined}
                />
              )}
            />
            <Controller
              control={control}
              name="warehouse_id"
              render={({ field, fieldState }) => (
                <Select
                  label={m.route_sr_new_field_warehouse()}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  options={warehouseOptions}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="period"
              render={({ field }) => (
                <DateInput
                  label={m.route_sr_new_field_period()}
                  mode="month"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  hint={m.route_sr_new_period_hint()}
                />
              )}
            />
          </div>
        </section>
      )}

      {step === 2 && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>{m.route_sr_new_lines_heading()}</h2>
          <p className={styles.hint}>{m.route_sr_new_lines_hint()}</p>
          {/* Гейт F59 (E-UX-2): у «Учётного» назван источник — склад и дата журнала. */}
          <p className={styles.provenance}>
            {m.route_sr_new_lines_provenance({
              warehouse: warehouseOptions.find((o) => o.value === warehouseId)?.label ?? '—',
              date: formatDate(new Date().toISOString()),
            })}
          </p>
          <div className={[shared.scrollX, styles.linesFrame].join(' ')}>
            <table className={styles.linesTable}>
              <thead>
                <tr>
                  <th className={styles.colItem}>{m.route_sr_new_col_item()}</th>
                  <th className={styles.colBatch}>{m.route_sr_new_col_batch()}</th>
                  <th className={[styles.colExpected, styles.thNum].join(' ')}>
                    {m.route_sr_new_col_expected()}
                  </th>
                  <th className={[styles.colActual, styles.thNum].join(' ')}>
                    {m.route_sr_new_col_actual()}
                  </th>
                  <th className={[styles.colVariance, styles.thNum].join(' ')}>
                    {m.route_sr_new_col_variance()}
                  </th>
                  <th className={styles.colUom}>{m.route_sr_new_col_uom()}</th>
                  <th className={styles.colReason}>{m.route_sr_new_col_reason()}</th>
                  <th className={styles.removeCell} aria-label={m.route_sr_new_col_remove_aria()} />
                </tr>
              </thead>
              <tbody>
                {linesArr.fields.map((f, idx) => {
                  const l = lines[idx] ?? emptyLine();
                  const varianceDec = computeVariance(l);
                  const variance = varianceDec.toNumber();
                  return (
                    <tr key={f.id}>
                      <td>
                        <Controller
                          control={control}
                          name={`lines.${idx}.item_id` as const}
                          render={({ field, fieldState }) => (
                            <ItemPicker
                              label=""
                              value={field.value}
                              onChange={(v) => {
                                field.onChange(v);
                                void loadExpectedQty(idx, v);
                              }}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </td>
                      <td>
                        <Input
                          label=""
                          placeholder="—"
                          className={styles.cellField}
                          {...register(`lines.${idx}.batch_no` as const)}
                        />
                      </td>
                      {/*
                        F37 (WHS-F37): учётный остаток — значение системы, не ввод
                        оператора. Показывается тремя знаками, полное — в подсказке;
                        на сервер уходит точное: отклонение считается от него.
                      */}
                      <td className={styles.numCell}>
                        <span
                          className={[shared.mono, styles.expectedQty].join(' ')}
                          title={l.expected_qty}
                        >
                          {formatDecimal(l.expected_qty, { maxDecimals: 3 })}
                        </span>
                      </td>
                      <td className={styles.numCell}>
                        {/* Гейт F59 (E-UI-5): факт — число: моноширинный, по правому краю,
                            чтобы сравнивался с учётным разряд в разряд. */}
                        <Input
                          label=""
                          inputMode="decimal"
                          className={[styles.cellField, styles.numField].join(' ')}
                          {...register(`lines.${idx}.physical_qty` as const)}
                        />
                      </td>
                      <td
                        className={[
                          styles.varianceCell,
                          variance > 0 ? styles.variancePositive : '',
                          variance < 0 ? styles.varianceNegative : '',
                          variance === 0 ? styles.varianceZero : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {formatSignedQty(varianceDec)}
                      </td>
                      <td>
                        {/* Гейт F59 (E-UX-1): единица — из карточки позиции, не выбор
                            оператора; селект не узнавал «кг» и показывал «—». */}
                        <span className={styles.uomText}>{formatUom(l.uom) || '—'}</span>
                      </td>
                      <td>
                        <Controller
                          control={control}
                          name={`lines.${idx}.variance_reason` as const}
                          render={({ field }) => (
                            <Select
                              label=""
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              options={varianceOptions()}
                            />
                          )}
                        />
                      </td>
                      <td className={styles.removeCell}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => linesArr.remove(idx)}
                          disabled={linesArr.fields.length <= 1}
                          aria-label={m.route_sr_new_row_remove_aria()}
                          leadingIcon={<Trash2 size={14} />}
                        >
                          {''}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Гейт F59 (E2-UX-3): обязательность причины названа до создания черновика. */}
          <p className={styles.ruleHint}>{m.route_sr_new_reason_rule()}</p>
          <div className={styles.addRow}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={<Plus size={14} />}
              onClick={() => linesArr.append(emptyLine())}
            >
              {m.route_sr_new_btn_add_row()}
            </Button>
          </div>
          <Input
            label={m.route_sr_new_notes_label()}
            placeholder={m.route_sr_new_notes_placeholder()}
            {...register('notes')}
            error={errors.notes?.message}
          />
        </section>
      )}

      {step === 3 && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>{m.route_sr_new_preview_heading()}</h2>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{m.route_sr_new_summary_rows()}</span>
              <span className={styles.summaryValue}>{lines.length}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{m.route_sr_new_summary_total_variance()}</span>
              <span
                className={[
                  styles.summaryValue,
                  totalVariance > 0
                    ? styles.variancePositive
                    : totalVariance < 0
                      ? styles.varianceNegative
                      : styles.varianceZero,
                ].join(' ')}
              >
                {formatSignedQty(totalVarianceDec)}
              </span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{m.route_sr_new_summary_severity_map()}</span>
              <div className={styles.severityMap}>
                <span className={[styles.severityChip, styles.severityMatch].join(' ')}>
                  {m.route_sr_new_severity_match_chip({ count: varianceCounts.match })}
                </span>
                <span className={[styles.severityChip, styles.severityWarn].join(' ')}>
                  {m.route_sr_new_severity_warn_chip({ count: varianceCounts.warn })}
                </span>
                <span className={[styles.severityChip, styles.severityDanger].join(' ')}>
                  {m.route_sr_new_severity_danger_chip({ count: varianceCounts.danger })}
                </span>
              </div>
            </div>
          </div>
          <p className={styles.hint}>{m.route_sr_new_preview_footnote()}</p>
        </section>
      )}

      {step === 4 && (
        <section className={styles.section}>
          {/* Гейт F59 (E2-UI-5, E2-UX-1): значок в строке с заголовком; документ
              назван номером. */}
          <h2 className={[styles.sectionHeading, styles.doneHeading].join(' ')}>
            <CheckCircle2 size={20} className={styles.successIcon} aria-hidden="true" />
            {draftNo
              ? m.route_sr_new_draft_created_named({ no: draftNo })
              : m.route_sr_new_draft_created_heading()}
          </h2>
          {createMut.isPending && <p className={styles.hint}>{m.route_sr_new_saving_hint()}</p>}
          {createMut.isError && (
            <div className={[shared.inlineAlert, shared.inlineAlertDanger].join(' ')}>
              {m.route_sr_new_save_failed()}
            </div>
          )}
          {draftId && previewQuery.data && (
            <>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>
                    {m.route_sr_new_preview_items_affected()}
                  </span>
                  <span className={styles.summaryValue}>
                    {previewQuery.data.summary?.items_affected ?? 0}
                  </span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>
                    {m.route_sr_new_preview_total_variance()}
                  </span>
                  <span className={styles.summaryValue}>
                    {formatMoney(previewQuery.data.summary?.total_variance_value ?? null)}
                  </span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>
                    {m.route_sr_new_preview_max_variance()}
                  </span>
                  <span className={styles.summaryValue}>
                    {formatDecimal(maxVarianceOf(previewQuery.data.lines), { maxDecimals: 3 })}
                  </span>
                </div>
              </div>
              {/* Раунд 12 — та же фиксированная раскладка, что у первого
                  читателя: ширину назначает таблица, «Позиция» забирает
                  остаток. Иначе неразрывные числа выдавливают критичность за
                  край, и правка снова достаётся одному экрану из двух. */}
              <Table
                layout="fixed"
                ariaLabel={m.route_sr_new_preview_lines_aria()}
                data={previewQuery.data.lines ?? []}
                rowKey={(_, i) => `p-${i}`}
                columns={[
                  {
                    /*
                     * Второй читатель догоняет первый (раунды 7 и 11 гейта C).
                     *
                     * `mono` на колонке идентичности — наследство времён хеша:
                     * с полным наименованием моноширинный набор съедал полширины
                     * и выдавливал критичность. Внутри `EntityRefCell` артикул и
                     * так моноширинный. Колонка остаётся поглотителем — лишнюю
                     * ширину забирает имя, а не пустая колонка партии.
                     */
                    id: 'item',
                    header: m.route_sr_new_col_item(),
                    accessor: (l) => <EntityRefCell name={l.item_label} code={l.item_sku} />,
                  },
                  {
                    id: 'batch',
                    width: '130px',
                    header: m.route_sr_new_col_batch(),
                    // Партия — номер, а не срез идентификатора (раунд 9 гейта C).
                    accessor: (l) => l.batch_no ?? '—',
                    mono: true,
                    nowrap: true,
                  },
                  {
                    id: 'exp',
                    width: '112px',
                    header: m.route_sr_new_col_expected(),
                    // Числа — тем же форматтером, что в реестрах: сырой Decimal
                    // шёл без разрядов и без локали (раунд 3 гейта C, правка
                    // дошла тогда только до реестра).
                    accessor: (l) => formatDecimal(l.expected_qty),
                    mono: true,
                    align: 'right',
                    nowrap: true,
                  },
                  {
                    id: 'phys',
                    width: '90px',
                    header: m.route_sr_new_col_actual(),
                    accessor: (l) => formatDecimal(l.counted_qty),
                    mono: true,
                    align: 'right',
                    nowrap: true,
                  },
                  {
                    id: 'var',
                    width: '130px',
                    header: m.route_sr_new_col_variance(),
                    // Гейт F59 (E2-UI-3): Δ окрашена так же, как на шаге 2.
                    accessor: (l) => (
                      <span
                        className={
                          Number(l.variance_qty) > 0
                            ? styles.variancePositive
                            : Number(l.variance_qty) < 0
                              ? styles.varianceNegative
                              : styles.varianceZero
                        }
                      >
                        {formatDecimal(l.variance_qty)}
                      </span>
                    ),
                    mono: true,
                    align: 'right',
                    nowrap: true,
                  },
                  {
                    id: 'sev',
                    header: m.route_sr_col_severity(),
                    width: '152px',
                    nowrap: true,
                    /*
                     * Раунд 7 гейта C — ВТОРОЙ предпросмотр догоняет первый.
                     *
                     * Правки чипа, глифа и подписи ушли только в реестр, а тот
                     * же предпросмотр внутри мастера остался с голым текстом.
                     * Ровно тот класс, что уже стоил паку регресса: «поменяли
                     * одного читателя из двух». Читателей у этой таблицы два,
                     * и выглядеть они обязаны одинаково.
                     */
                    accessor: (l) => (
                      <>
                        {/* Гейт F59 (E2-UI-1): пилюля — стилями реестра (один источник);
                            в модуле мастера класса не было, и значок с текстом
                            разъезжались на две строки. */}
                        <span className={registryStyles.severity} data-sev={l.severity}>
                          {l.severity === 'critical_variance' ? (
                            <ShieldAlert size={12} aria-hidden="true" />
                          ) : l.severity === 'variance' ? (
                            <AlertTriangle size={12} aria-hidden="true" />
                          ) : (
                            <Check size={12} aria-hidden="true" />
                          )}
                          {severityLabel(l.severity)}
                        </span>
                      </>
                    ),
                  },
                ]}
              />
              {/* Гейт F59 (E2-UX-2): черновик сохранён, но не готов к проведению —
                  называем, чего не хватает и где это сделать. */}
              {missingReasonsAfterSave > 0 && (
                <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')}>
                  <AlertTriangle size={16} aria-hidden="true" />{' '}
                  {m.route_sr_new_done_reasons_next({ count: String(missingReasonsAfterSave) })}
                </div>
              )}
            </>
          )}
        </section>
      )}

      <div className={`${styles.footer} ${shared.stickyFooter}`}>
        <div>
          {step > 1 && step < 4 && (
            <Button
              type="button"
              variant="ghost"
              onClick={goBack}
              leadingIcon={<ArrowLeft size={14} />}
            >
              {m.common_button_back()}
            </Button>
          )}
        </div>
        <div className={styles.footerRight}>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate({ to: '/stock-reconciliation' })}
          >
            {step === 4 ? m.route_sr_new_btn_to_list() : m.common_button_cancel()}
          </Button>
          {step < 3 && (
            <Button
              type="button"
              variant="primary"
              onClick={() => void goNext()}
              trailingIcon={<ArrowRight size={14} />}
            >
              {m.common_button_next()}
            </Button>
          )}
          {step === 3 && (
            <Button
              type="button"
              variant="primary"
              onClick={() => void goNext()}
              disabled={createMut.isPending}
              loading={createMut.isPending}
            >
              {m.route_lc_new_btn_create()}
            </Button>
          )}
          {/* Гейт F59 (E2-UX-1): главное действие — открыть созданный черновик;
              «К списку» — вторичное слева, «Готово» дублировало его. */}
          {step === 4 && draftId && (
            <Button
              type="button"
              variant="primary"
              onClick={() => navigate({ to: '/stock-reconciliation/$id', params: { id: draftId } })}
            >
              {m.route_sr_new_btn_open_draft()}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
